/**
 * @file: AiEditionTabBar.tsx
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { useChatUnreadCount } from '@euler/app/flows/chat/lib/session/hooks/useChatUnreadCount';
import { useFeedUnreadCount } from '@euler/app/flows/feed/lib/unread/hooks/useFeedUnreadCount';
import { useTabBarHeight } from '@euler/app/flows/home/components/TabBar';
import { TabBarItem } from '@euler/app/flows/home/components/TabBarItem';
import { UnreadBadge } from '@euler/components/UnreadBadge';
import { useStoreFeatureLookup } from '@euler/functions/entitlements/useStoreFeature';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { BottomTabBarProps } from '@react-navigation/bottom-tabs';
import { FC, memo } from 'react';
import { View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { kAiEditionTabs } from './aiEditionTabs';

/** The phone's bottom bar for the AI edition: its tabs the store has. */
export const AiEditionTabBar: FC<BottomTabBarProps> = memo(
  ({ navigation, state }) => {
    const isDesktop = useIsDesktopLayout();
    const { rpx } = useResponsiveContext();
    const height = useTabBarHeight();
    const { bottom } = useSafeAreaInsets();
    const featureOf = useStoreFeatureLookup();
    const inboxUnreadCount = useFeedUnreadCount() + useChatUnreadCount();
    const activeTabName = state.routes[state.index].name;

    // The sidebar hosts the tabs on desktop.
    if (isDesktop) return null;

    return (
      <View
        css={`
          height: ${height}px;
          flex-direction: row;
          align-items: stretch;
          justify-content: space-around;
          padding-bottom: ${Math.max(rpx(10), bottom)}px;
          padding-top: ${rpx(10)}px;
          background-color: #ffffff;
          border-top-width: 1px;
          border-top-color: #e5e7eb;
        `}
      >
        {kAiEditionTabs
          .filter(tab => tab.feature == null || featureOf(tab.feature).enabled)
          .map(tab => (
            <TabBarItem
              key={tab.name}
              name={tab.name}
              activeTabName={activeTabName}
              text={tab.label}
              icon={tab.renderIcon}
              iconSize={rpx(22)}
              textSize={rpx(10)}
              badge={
                tab.showUnread ? (
                  <UnreadBadge count={inboxUnreadCount} variant="overlay" />
                ) : null
              }
              onPress={() => navigation.navigate(tab.name, {})}
            />
          ))}
      </View>
    );
  },
);
